import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MASTERCHART_DRAWER_MATCH_PATHS,
  masterChartPath,
} from "constants/masterChartRoutes";
import {
  List,
  ListSubheader,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import CampaignRounded from "@mui/icons-material/CampaignRounded";
import ChatRounded from "@mui/icons-material/ChatRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import MedicalInformationRounded from "@mui/icons-material/MedicalInformationRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import PersonSearch from "@mui/icons-material/PersonSearch";
import QuestionAnswerRounded from "@mui/icons-material/QuestionAnswerRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import SmsRounded from "@mui/icons-material/SmsRounded";
import VerifiedRounded from "@mui/icons-material/VerifiedRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import ExitToAppRounded from "@mui/icons-material/ExitToAppRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import NoteAltRounded from "@mui/icons-material/NoteAltRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import AssignmentRounded from "@mui/icons-material/AssignmentRounded";
import FactCheckRounded from "@mui/icons-material/FactCheckRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import RecordVoiceOverRounded from "@mui/icons-material/RecordVoiceOverRounded";
import AssistantRounded from "@mui/icons-material/AssistantRounded";
import NavListItem from "components/common/NavListItem";
import { ChatContext } from "context/Chat";
import { DrawerContext } from "context/DrawerContext";
import { useChat } from "components/chatbot/ContextBox/useChat";
import { useAuth } from "hooks/useAuth";
import { getTrialLengthForRole } from "constants/trials";

/** Small caps heading over a group of nav rows; hidden on the mini rail. */
const NavSectionLabel = ({ children }) => {
  const open = useContext(DrawerContext);
  if (!open) return null;
  return (
    <ListSubheader
      component="div"
      disableSticky
      sx={{ bgcolor: "transparent", typography: "overline", lineHeight: 2.5, px: 3 }}
    >
      {children}
    </ListSubheader>
  );
};

// CareMap has no sidebar entry on purpose: it is its own full-screen app outside
// the dashboard, so it lives in the nine-dot launcher only (AppSwitcher.jsx).
export const PatientDrawer = ({ subscription, userData }) => {
  const navigate = useNavigate();
  const { newThread } = useContext(ChatContext);
  const { messages } = useChat();
  const { canStartTrial, hasActiveTrial, hasActiveDailyPass } = useAuth();
  const disabled = !messages || messages?.length < 1;
  const [showNoEmail, setShowNoEmail] = useState(false);
  const trialLengthDays = getTrialLengthForRole(userData?.role);
  const { t } = useTranslation();

  // [BETA-KIJABE] Temporary flag for Kijabe beta testers — remove when beta period ends.
  // To activate: set `betaAccess: true` on the user's Firestore doc.
  const isBetaKijabe = Boolean(userData?.betaAccess);

  const handleClose = () => setShowNoEmail(false);

  const copyChat = async () => {
    try {
      const messagesArray = messages.map(
        (message) =>
          `${message.sender === "user" ? "You" : "My HealthDesk"}:\n${message.message
          }`
      );
      await navigator.clipboard.writeText(messagesArray.join("\n\n"));
      console.log("Content copied to clipboard");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const sendEmail = () => {
    if (disabled) return;

    const messagesArray = messages.map(
      (message) =>
        `${message.sender === "user" ? "You" : "My HealthDesk"}:\n${message.message
        }`
    );

    window.open(
      "mailto:?subject=HealthDesk Medical SuperIntelligence Transcript&body=" +
      encodeURIComponent(messagesArray.join("\n\n"))
    );

    let t = setTimeout(() => setShowNoEmail(true), 500);
    window.addEventListener("blur", () => clearTimeout(t));
  };

  const navigateNewThread = () => {
    if (disabled) return;
    navigate("/dashboard");
    newThread();
  };

  return (
    <>
      <EmailDialog open={showNoEmail} onClose={handleClose} onCopy={copyChat} />
      <List subheader={<NavSectionLabel>{t("nav.sections.care")}</NavSectionLabel>}>
        <NavListItem
          icon={<AssistantRounded />}
          text={t("nav.assistant")}
          secondary={t("nav.assistantHint")}
          link="/dashboard/assistant"
        />
        <NavListItem
          icon={<RecordVoiceOverRounded />}
          text={t("nav.careStart")}
          hint={t("nav.careStartHint")}
          link="/dashboard/carestart"
        />
        <NavListItem
          icon={<SchoolRounded />}
          text={t("nav.medicalLibrary")}
          hint={t("nav.medicalLibraryHint")}
          link="/dashboard/medical-library"
        />
      </List>
      <List subheader={<NavSectionLabel>{t("nav.sections.records")}</NavSectionLabel>}>
        <NavListItem
          icon={<HealthAndSafetyRounded />}
          text={t("nav.masterChart")}
          hint={t("nav.masterChartHint")}
          link={masterChartPath()}
        />
        <NavListItem
          icon={<MedicationRounded />}
          text={t("nav.medications")}
          link="/dashboard/medications"
        />
        {/* TODO: add "Medication Tracking" here once re-enabled (post Twilio BAA). */}
        <NavListItem
          icon={<QrCode2Rounded />}
          text={t("nav.share")}
          hint={t("nav.shareHint")}
          link={masterChartPath("share")}
        />
        {/* TODO: Messages (facility <> patient comms) disabled for beta launch — re-enable when facility role ships */}
        {/* <NavListItem
          icon={<EmailRounded />}
          text="Messages"
          hint="Care messages from your team"
          link="/dashboard/messages"
        /> */}
        <NavListItem
          icon={<AssignmentRounded />}
          text={t("nav.dischargeInstructions")}
          hint={t("nav.dischargeInstructionsHint")}
          link="/dashboard/discharge-instructions"
        />
        {/* TODO: Medication Tracking disabled until Twilio BAA is in place */}
        {/* <NavListItem
          icon={<TrendingUpRounded />}
          text="Medication Tracking"
          link="/dashboard/medication-tracking"
        /> */}

        {/* TODO: Upgrade/trial prompt disabled for beta launch — nobody in the
            beta is being asked to pay, and the entry was already hidden for
            [BETA-KIJABE] users. Re-enable after the beta. */}
        {/* {!isBetaKijabe && !subscription && !hasActiveTrial && !hasActiveDailyPass && (
          <NavListItem
            icon={<PersonSearch />}
            text={
              canStartTrial ? "Start Free Trial" : "Upgrade to Commercial Free"
            }
            secondary={
              canStartTrial
                ? `Get full access for ${trialLengthDays} days`
                : undefined
            }
            noSelect
            link="/dashboard/upgrade"
            sx={
              canStartTrial
                ? {
                  "& .MuiListItemText-primary": {
                    color: "primary.main",
                    fontWeight: "medium",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "primary.main",
                  },
                }
                : undefined
            }
          />
        )} */}
      </List>
    </>
  );
};

export const ProfessionalDrawer = ({ subscription, userData }) => {
  const { canStartTrial, hasActiveTrial, hasActiveDailyPass } = useAuth();
  const trialLengthDays = getTrialLengthForRole(userData?.role);

  // [BETA-KIJABE] Temporary flag for Kijabe beta testers — remove when beta period ends.
  // To activate: set `betaAccess: true` on the user's Firestore doc.
  const isBetaKijabe = Boolean(userData?.betaAccess);

  return (
    <>
      <List subheader={<NavSectionLabel>Clinical</NavSectionLabel>}>
        {/*
          ChartMind link — navigates back to the live encounter (preserved across navigation
          by ChartMindSessionProvider). To start fresh, the page itself exposes a "New
          Encounter" button.
        */}
        <NavListItem
          icon={<NoteAltRounded />}
          text="ChartMind"
          hint="Patient encounters made easy"
          link="/dashboard/chartmind"
        />
        <NavListItem
          icon={<ForumRounded />}
          text="PeerView Case Consultation"
          hint="Ask about your encounter"
          link="/dashboard/peerview"
        />
        {/* [BETA-KIJABE] Hidden for Kijabe beta users (no session persistence) */}
        {!isBetaKijabe && (
          <NavListItem
            icon={<HistoryRounded />}
            text="My Charts"
            hint="View saved encounters"
            link="/dashboard/chartmind/sessions"
          />
        )}
      </List>
      {/* Only when something is in it, so there's never a lone heading.
          MorningReport is off for professionals for now; learners have it. */}
      {isBetaKijabe && (
        <List subheader={<NavSectionLabel>Education</NavSectionLabel>}>
          {/* [BETA-KIJABE] Shown only for Kijabe beta users. */}
          {isBetaKijabe && (
            <NavListItem
              icon={<QuestionAnswerRounded />}
              text="Ask Me Anything"
              hint="Medical Q&A"
              link="/dashboard/ask-me-anything"
            />
          )}
        </List>
      )}
      {/* TODO: Upgrade/trial prompt disabled for beta launch — see PatientDrawer. */}
      {/* {!isBetaKijabe && !subscription && !hasActiveTrial && !hasActiveDailyPass && (
        <>
          <List>
            <NavListItem
              icon={<PersonSearch />}
              text={
                canStartTrial
                  ? "Start Free Trial"
                  : "Upgrade to Commercial Free"
              }
              secondary={
                canStartTrial
                  ? `Get full access for ${trialLengthDays} days`
                  : undefined
              }
              noSelect
              link="/dashboard/upgrade"
              sx={
                canStartTrial
                  ? {
                    "& .MuiListItemText-primary": {
                      color: "primary.main",
                      fontWeight: "medium",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "primary.main",
                    },
                  }
                  : undefined
              }
            />
          </List>
        </>
      )} */}
    </>
  );
};

/** MorningReport learners see only the teaching cases. */
export const LearnerDrawer = () => (
  <List>
    <NavListItem
      icon={<SchoolRounded />}
      text="MorningReport"
      link="/dashboard/morning-report"
    />
  </List>
);

export const AdminDrawer = ({ isGlobalAdmin }) => (
  <>
    {isGlobalAdmin && (
      <>
        <List>
          <NavListItem
            icon={<PsychologyRounded />}
            text="Prompts"
            link="/dashboard/prompts"
          />
          <NavListItem
            icon={<VerifiedRounded />}
            text="Location Approval"
            link="/dashboard/admin/approval"
          />
          {/* Assistant Manager hidden from nav (legacy OpenAI-assistant system); page/route still exists.
              <NavListItem
                icon={<SmartToyRounded />}
                text="Assistant Manager"
                link="/dashboard/admin/assistants"
              /> */}
          <NavListItem
            icon={<BarChartRounded />}
            text="Usage Monitor"
            link="/dashboard/admin/usage"
          />
        </List>
      </>
    )}
  </>
);

/** Platform super-admin: JWT admin claim only (see isGlobalAdmin). Regional admins UI is exclusive to this drawer. */
export const GlobalAdminDrawer = () => (
  <>
    <List subheader={<NavSectionLabel>Content</NavSectionLabel>}>
      <NavListItem
        icon={<PsychologyRounded />}
        text="Prompts"
        link="/dashboard/prompts"
      />
      <NavListItem
        icon={<LocalHospitalRounded />}
        text="MorningReport Cases"
        link="/dashboard/admin/morning-report-cases"
      />
    </List>
    <List subheader={<NavSectionLabel>People</NavSectionLabel>}>
      <NavListItem
        icon={<VerifiedRounded />}
        text="Location Approval"
        link="/dashboard/admin/approval"
      />
      <NavListItem
        icon={<PeopleRounded />}
        text="Regional admins"
        link="/dashboard/admin/regional-admins"
      />
    </List>
    <List subheader={<NavSectionLabel>Monitoring</NavSectionLabel>}>
      {/* Assistant Manager hidden from nav (legacy OpenAI-assistant system); page/route still exists.
          <NavListItem
            icon={<SmartToyRounded />}
            text="Assistant Manager"
            link="/dashboard/admin/assistants"
          /> */}
      <NavListItem
        icon={<BarChartRounded />}
        text="Usage Monitor"
        link="/dashboard/admin/usage"
      />
      <NavListItem
        icon={<MonitorHeartRounded />}
        text="System Health"
        link="/dashboard/admin/health"
      />
      <NavListItem
        icon={<FactCheckRounded />}
        text="HIPAA Compliance"
        link="/dashboard/admin/hipaa-compliance"
      />
    </List>
  </>
);

/** Regional prompt editor: prompts + shared nav only (no platform admin tools). */
export const RegionalAdminDrawer = () => (
  <>
    <List>
      <NavListItem
        icon={<PsychologyRounded />}
        text="Prompts"
        link="/dashboard/prompts"
      />
    </List>
  </>
);

export const ChartMindManagerDrawer = () => (
  <>
    <List>
      <NavListItem
        icon={<PsychologyRounded />}
        text="Prompts"
        hint="Manage AI prompts"
        link="/dashboard/prompts"
      />
      <NavListItem
        icon={<BusinessRounded />}
        text="Team"
        hint="Manage your team"
        link="/dashboard/team"
      />
      {/* No longer hidden from beta admin orgs: the page counts encounters and charts from
          the ChartMind step counters every org writes, not the tokens beta orgs never had. */}
      <NavListItem
        icon={<BarChartRounded />}
        text="Usage"
        hint="Encounters and team activity"
        link="/dashboard/usage"
      />
    </List>
  </>
);

export const FacilityDrawer = ({ activeSubscriptionRole, userData }) => (
  <>
    {/* TODO: the facility role is held back for now, so this whole first nav
        group is gone and a clinic lands on Home instead. The two entries
        went for different reasons, and come back separately.

        Status Board: hidden with the rest of the role. DashboardRoutes.jsx
        shows Home at /dashboard to match, so swap that index route back along
        with this entry.

        Schedule (the Wait Time Scheduler): already hidden before this. The page
        renders behind PremiumWrapper and leads with PremiumAlert, and premium
        upgrading is switched off everywhere else, so it could only ever show
        its preview state. Bring it back only when premium upgrading returns.

        The whole group is commented rather than the two entries individually:
        an empty List still renders its Divider, which is a stray rule at the
        top of the drawer.

    <List>
      <NavListItem
        icon={<ScheduleRounded />}
        text="Status Board"
        hint="Wait times and capabilities"
        link="/dashboard"
      />
      <NavListItem
        icon={<CalendarMonthRounded />}
        text="Schedule"
        hint="Your opening hours"
        link="/dashboard/schedule"
      />
    </List>
    */}
    <List>
      <NavListItem
        icon={<FactCheckRounded />}
        text="Check-ins"
        hint="Answers before they arrive"
        link="/dashboard/visitstart"
        // Stays lit on a single check-in, which is this section, not elsewhere.
        matchPathPrefix
      />
      {/* TODO: disabled for beta launch — re-enable after it. Three entries,
          three separate reasons:

          Messages (facility <> patient comms): nothing reads what it sends. The
          patient's MessagesPage, its route and its nav entry are all commented
          out for the same reason, so a broadcast lands in
          users/{uid}/clinicMessages and no one ever sees it. Put both halves back
          at once.

          Virtual Queue and Virtual Registration send PHI (phone numbers) over SMS
          via Twilio. They were gated behind isDevEnvironment() pending the Twilio
          BAA; commented out now so beta clinics are not shown two more ways to
          check a patient in beside the one that works. */}
      {/* <NavListItem
        icon={<ForumRounded />}
        text="Messages"
        link="/dashboard/messaging"
      /> */}
      {/* {isDevEnvironment() && (
        <NavListItem
          icon={<AssignmentTurnedInRounded />}
          text="Virtual Queue"
          link="/dashboard/queue"
        />
      )}
      {isDevEnvironment() && (
        <NavListItem
          icon={<SmsRounded />}
          text="Virtual Registration"
          link="/dashboard/virtual-registration"
        />
      )} */}
    </List>
    {/* TODO: Advertise hidden for now — re-enable when ready. */}
    {/* <List>
      <NavListItem
        icon={<CampaignRounded />}
        text="Advertise"
        link="/dashboard/advertising"
      />
    </List>
    <Divider /> */}
    {/* TODO: Upgrade/billing prompt disabled for beta launch — beta clinics are
        not being asked to pay. Re-enable after it. */}
    {/* {!activeSubscriptionRole &&
      userData?.role === "facility" &&
      !userData?.betaAccess && (
        <>
          <List>
            <NavListItem
              icon={<MedicalInformationRounded />}
              text="Upgrade to CareMap Plus"
              link="/dashboard/upgrade"
            />
          </List>
        </>
      )} */}
  </>
);

// Opened from the patient drawer, so its copy is translated ("home" namespace).
const EmailDialog = ({ open, onClose, onCopy }) => {
  const { t } = useTranslation("home");
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("emailDialog.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("emailDialog.body")}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onCopy}>
          {t("emailDialog.copy")}
        </Button>
        <Button onClick={onClose} autoFocus>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
